import * as React from 'react'

/**
 * A hook to handle delayed hover interactions that include 'warm hover' behavior (reopen instantly if recently closed).
 *
 * This is useful for UI elements like popovers or tooltips where:
 * - You want to delay opening on hover to avoid accidental triggers.
 * - You want to delay closing to allow brief mouse-out without flicker.
 * - You want to reopen instantly if the user hovers again shortly after closing.
 *
 * @param onOpen - Callback to trigger when hover "open" should occur.
 * @param onClose - Callback to trigger when hover "close" should occur.
 * @param openDelayDuration - Delay in milliseconds before opening. Defaults to 1000ms.
 * @param closeDelayDuration - Delay in milliseconds before closing. Defaults to 800ms.
 * @param isOpen - Boolean indicating whether the popover is currently open.
 *
 * @returns An object containing:
 *   - handleMouseEnter: mouse enter handler with delayed or instant open.
 *   - handleMouseLeave: mouse leave handler with delayed close.
 */
export function useDelayedHover({
  onOpen,
  onClose,
  openDelayDuration = 800,
  closeDelayDuration = 500,
  isOpen,
}: {
  onOpen: () => void
  onClose: () => void
  openDelayDuration?: number
  closeDelayDuration?: number
  isOpen?: boolean
}): {
  handleMouseEnter: () => void
  handleMouseLeave: () => void
} {
  const openTimeoutRef = React.useRef<NodeJS.Timeout>()
  const closeTimeoutRef = React.useRef<NodeJS.Timeout>()
  const lastCloseTimeRef = React.useRef<number | null>(null)

  const handleMouseEnter = (): void => {
    // Clear any pending close timeout to prevent closing if user quickly returns
    clearTimeout(closeTimeoutRef.current)

    const now = Date.now()
    const recentlyClosed =
      lastCloseTimeRef.current && now - lastCloseTimeRef.current < 1000

    // Only attempt to open if the popover is currently closed
    if (!isOpen) {
      if (recentlyClosed) {
        // "Warm hover": If the popover was just closed recently (within 1s), open immediately
        onOpen()
      } else {
        // Otherwise, open after a delay
        openTimeoutRef.current = setTimeout(() => {
          onOpen()
        }, openDelayDuration)
      }
    }
  }

  const handleMouseLeave = (): void => {
    // Clear any pending open timeout since the user is no longer hovering
    clearTimeout(openTimeoutRef.current)

    // Schedule a delayed close after the user stops hovering
    closeTimeoutRef.current = setTimeout(() => {
      // Track when the popover was last closed to support "warm hover"
      lastCloseTimeRef.current = Date.now()
      onClose()
    }, closeDelayDuration)
  }

  React.useEffect(() => {
    return (): void => {
      clearTimeout(openTimeoutRef.current)
      clearTimeout(closeTimeoutRef.current)
    }
  }, [])

  return {
    handleMouseEnter,
    handleMouseLeave,
  }
}
